<template>
    <div class="payment-schedule">
        <div class="container-width">
            <h2 class="section-title">Amortization Breakdown</h2>
            <p class="sub-heading">Use this chart to see how much principal and interest you are paying with each payment</p>
            <div class="payment-chart">
                <div class="bar-chart">
                    <div>
                        <vue-chart
                            type="bar"
                            :data="chartData"
                            :width="ChartWidth"
                            :height="ChartHeight"
                            :options="chartOptions()"
                        />
                    </div>
                </div>
            </div>
            <div class="chart-details">
                <ul class="chart-info">
                    <li><span class="green-bg"></span> Principal</li>
                    <li><span class="blue-bg"></span> Interest</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import AmortizationBreakdownHelper from '../helpers/amortization-breakdown';

    export default {
        props: {
            loanAmount: {
                default: 0,
            },
            loanTermInYears: {
                default: 0,
            },
            interestRate: {
                default: 0,
            },
            balanceValues: {
                type: Object,
                default() {
                    return {
                        0: [25547.13, 25029.19, 24510.17, 23990.06, 23468.87, 22946.6, 22423.24, 21898.78, 21373.24, 20846.6, 20318.86, 19790.03, 19260.09, 18729.05, 18196.9, 17663.64, 17129.27, 16593.79, 16057.19, 15519.48, 14980.64, 14440.69, 13899.6, 13357.39, 12814.05, 12269.58, 11723.98, 11177.23, 10629.35, 10080.33, 9530.16, 8978.85, 8426.39, 7872.78, 7318.01, 6762.09, 6205.01, 5646.77, 5087.37, 4526.8, 3965.06, 3402.15, 2838.07, 2272.82, 1706.39, 1138.77, 569.98, 0],
                    };
                },
            },
            dateValues: {
                type: Object,
                default() {
                    return {
                        0: ['Jan, 2018', 'Feb, 2018', 'Mar, 2018', 'Apr, 2018', 'May, 2018', 'Jun, 2018', 'Jul, 2018', 'Aug, 2018', 'Sep, 2018', 'Oct, 2018', 'Nov, 2018', 'Dec, 2018', 'Jan, 2019', 'Feb, 2019', 'Mar, 2019', 'Apr, 2019', 'May, 2019', 'Jun, 2019', 'Jul, 2019', 'Aug, 2019', 'Sep, 2019', 'Oct, 2019', 'Nov, 2019', 'Dec, 2019', 'Jan, 2020', 'Feb, 2020', 'Mar, 2020', 'Apr, 2020', 'May, 2020', 'Jun, 2020', 'Jul, 2020', 'Aug, 2020', 'Sep, 2020', 'Oct, 2020', 'Nov, 2020', 'Dec, 2020', 'Jan, 2021', 'Feb, 2021', 'Mar, 2021', 'Apr, 2021', 'May, 2021', 'Jun, 2021', 'Jul, 2021', 'Aug, 2021', 'Sep, 2021', 'Oct, 2021', 'Nov, 2021', 'Dec, 2021'],
                    };
                },
            },
            dataSet: {
                type: Object,
                default() {
                    return {
                        principalValues: [516.87, 517.94, 519.02, 520.1, 521.19, 522.27, 523.36, 524.45, 525.54, 526.64, 527.74, 528.84, 529.94, 531.04, 532.15, 533.26, 534.37, 535.48, 536.6, 537.71, 538.84, 539.96, 541.08, 542.21, 543.34, 544.47, 545.61, 546.74, 547.88, 549.02, 550.17, 551.31, 552.46, 553.61, 554.77, 555.92, 557.08, 558.24, 559.4, 560.57, 561.74, 562.91, 564.08, 565.25, 566.43, 567.61, 568.79, 569.98],
                        interestValues: [54.3, 53.22, 52.14, 51.06, 49.98, 48.89, 47.81, 46.72, 45.62, 44.53, 43.43, 42.33, 41.23, 40.13, 39.02, 37.91, 36.8, 35.69, 34.57, 33.45, 32.33, 31.21, 30.08, 28.96, 27.83, 26.7, 25.56, 24.42, 23.29, 22.14, 21.0, 19.85, 18.71, 17.55, 16.4, 15.25, 14.09, 12.93, 11.76, 10.6, 9.43, 8.26, 7.09, 5.91, 4.74, 3.55, 2.37, 1.19],
                    };
                },
            },
        },
        data() {
            return {
                ChartWidth: 1140,
                ChartHeight: 270,
                DataSet: this.dataSet,
                BalanceValues: this.balanceValues,
                DateValues: this.dateValues,
                chartData: {
                    labels: this.dataSet.labels,
                    datasets: [
                        {
                            label: 'Principal',
                            data: this.dataSet.principalValues,
                            backgroundColor: '#08c177',
                            borderColor: '#08c177',
                            borderWidth: 3,
                            fill: false,
                            borderJoinStyle: 'round',
                        },
                        {
                            label: 'Interest',
                            data: this.dataSet.interestValues,
                            backgroundColor: '#00aeef',
                            borderColor: '#00aeef',
                            borderWidth: 3,
                            fill: false,
                            borderJoinStyle: 'round',
                        },
                    ],
                },

            };
        },
        methods: {
            populateDataset() {
                const dataSet = {
                    labels: [],
                    principalValues: [],
                    interestValues: [],
                    balanceValues: [],
                };

                const calculatedData = Object.keys(this.amortizationBreakdown);
                calculatedData.forEach((element) => {
                    if (calculatedData.includes(element) >= 0) {
                        const monthKeys = Object.keys(this.amortizationBreakdown[element]);
                        monthKeys.forEach((month) => {
                            const monthData = this.amortizationBreakdown[element][month];
                            dataSet.labels.push(`${monthData.namedMonth.substring(0, 3)}, ${element}`);
                            dataSet.principalValues.push(monthData.principal);
                            dataSet.interestValues.push(monthData.interestPaid);
                            dataSet.balanceValues.push(monthData.balance);
                        });
                    }
                });
                return dataSet;
            },
            pushDataInChart() {
                this.$set(this.chartData, 'labels', this.DataSet.labels);
                this.$set(this.chartData, 'datasets', []);
                this.$set(this.BalanceValues, 0, this.DataSet.balanceValues);
                this.$set(this.DateValues, 0, this.DataSet.labels);
                this.chartData.datasets.push(
                    {
                        label: 'Principal',
                        data: this.DataSet.principalValues,
                        backgroundColor: '#c2e8de',
                        borderColor: '#c2e8de',
                        hoverBackgroundColor: '#08c177',
                        hoverBorderColor: '#08c177',
                        borderWidth: 3,
                        fill: false,
                        borderJoinStyle: 'round',
                    },
                    {
                        label: 'Interest',
                        data: this.DataSet.interestValues,
                        backgroundColor: '#99dff9',
                        borderColor: '#99dff9',
                        hoverBackgroundColor: '#00aeef',
                        hoverBorderColor: '#00aeef',
                        borderWidth: 3,
                        fill: false,
                        borderJoinStyle: 'round',
                    },
                );
            },
            chartOptions() {
                const dateValues = this.DateValues ? this.DateValues : 0;
                const balanceValues = this.BalanceValues ? this.BalanceValues : 0;
                return {
                    animation: {
                        duration: 1000,
                    },
                    responsive: false,
                    legend: {
                        display: false,
                    },
                    gridLines: false,
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback(label, index, labels) {
                                    if (Math.max(...labels) <= 10) {
                                        return `$${Accounting.formatNumber(label, { precision: 2 })}`;
                                    }
                                    return `$${Accounting.formatNumber(label)}`;
                                },
                            },
                            afterTickToLabelConversion(scaleInstance) {
                                scaleInstance.ticks[scaleInstance.ticks.length - 1] = null;
                            },
                        }],
                        xAxes: [{
                            afterTickToLabelConversion(data) {
                                const xLabels = data.ticks;

                                let lastLabel = 0;
                                xLabels.forEach((labels, i) => {
                                    if (i === 0) {
                                        xLabels[i] = labels.split(',')[1].trim();
                                        lastLabel = xLabels[i];
                                    } else if (labels.split(',')[1].trim() !== lastLabel) {
                                        xLabels[i] = labels.split(',')[1].trim();
                                        lastLabel = xLabels[i];
                                    } else {
                                        xLabels[i] = '';
                                    }
                                });
                            },
                            gridLines: {
                                drawOnChartArea: false,
                            },
                        }],
                    },
                    tooltips: {
                        enabled: false,
                        mode: 'index',
                        custom(tooltip) {
                            let tooltipEl = document.getElementById('tooltip-monthly-pay');

                            if (tooltipEl) {
                                tooltipEl.parentNode.removeChild(tooltipEl);
                            }

                            tooltipEl = document.createElement('div');
                            tooltipEl.id = 'tooltip-monthly-pay';
                            tooltipEl.innerHTML = '<div></div>';
                            this._chart.canvas.parentNode.appendChild(tooltipEl);

                            if (tooltip.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }

                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }
                            if (tooltip.body) {
                                let innerHtml = '';
                                const titleLines = tooltip.title || [];
                                const bodyLines = tooltip.body.map(getBody);
                                let elIndex = 0;
                                titleLines.forEach((title) => {
                                    innerHtml += `<h5>${title}</h5>`;
                                    elIndex = dateValues[0].indexOf(`${title}`);
                                });

                                const principal = bodyLines[0][0].split(':');
                                innerHtml += `<label>${principal[0]}: <b class="green-text">$${Accounting.formatNumber(principal[1], { precision: 2 })}</b></label>`;

                                const interest = bodyLines[1][0].split(':');
                                innerHtml += `<label>${interest[0]}: <b class="blue-text">$${Accounting.formatNumber(interest[1], { precision: 2 })}</b></label>`;

                                const balance = balanceValues[0][elIndex];

                                innerHtml += `<label>Balance: <b>$${Accounting.formatNumber(balance, { precision: 2 })}</b></label>`;
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 110 + tooltip.caretY}px`;
                            tooltipEl.style.height = '100px';
                        },
                        callbacks: {
                            title(toolTipItem) {
                                return this._data.labels[toolTipItem[0].index];
                            },
                        },
                    },
                };
            },
        },
        computed: {
            amortizationBreakdown() {
                return AmortizationBreakdownHelper.amortizationBreakdown(this.loanAmount, this.loanTermInYears, this.interestRate);
            },
        },
        mixins: [Accounting],
        watch: {
            loanAmount: {
                handler() {
                    this.DataSet = this.populateDataset();
                },
            },
            loanTermInYears: {
                handler() {
                    this.DataSet = this.populateDataset();
                },
            },
            interestRate: {
                handler() {
                    this.DataSet = this.populateDataset();
                },
            },
            DataSet: {
                handler() {
                    this.pushDataInChart();
                },
                deep: true,
            },
        },
        mounted() {
            this.DataSet = this.populateDataset();
            this.BalanceValues[0] = this.DataSet.balanceValues;
            this.DateValues[0] = this.DataSet.labels;
        },
        components: {
            VueChart,
        },
    };
</script>
<style lang="scss" src="../../assets/styles/amortization-breakdown-graph.scss"></style>
